import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { UseOutletContextAdmin } from './Admin';
import { StateLanguage } from '../../../languages/config/StateLanguage';
import { ApiDigitalAuthPolicy } from '../../../services/auth/policy.service';
import { ApiDigitalAuthResource } from '../../../services/auth/resource.service';
import { Statement, ResourceCatalog } from '../../../models/policy.model';
import { GlobalSwal } from '../../../styles/swal.style';
import { errorWebHandler } from '../../../scripts/error.web.handler.script';
import StatementBuilder from '../../../components/StatementBuilder';

export interface PolicyCreatePageProps {};

const PolicyCreatePage: React.FunctionComponent<PolicyCreatePageProps> = props => {
  const {setRoute, appServiceForUser} = UseOutletContextAdmin()
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [statements, setStatements] = useState<Statement[]>([{ effect: 'Allow', actions: [], resources: [] }])
  const [loading, setLoading] = useState(false)
  const [catalog, setCatalog] = useState<ResourceCatalog | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(true)

  const loadCatalog = async (api_key: string) => {
    setCatalogLoading(true)
    try {
      const response = await ApiDigitalAuthResource.getCatalog(api_key)
      if (response.status === 200 && response.data) {
        setCatalog(response.data)
      }
    } catch {
      // catalog load failure is non-blocking
    } finally {
      setCatalogLoading(false)
    }
  }

  const isValid = (): boolean => {
    if (!name.trim()) return false
    if (statements.length === 0) return false
    return statements.every(s => s.actions.length > 0 && s.resources.length > 0)
  }

  const handleSubmit = async () => {
    if (!appServiceForUser || !isValid()) return
    setLoading(true)

    await ApiDigitalAuthPolicy.createPolicy(appServiceForUser.api_key, {
      name: name.trim(),
      description: description.trim(),
      statements
    }).then((response) => {
      if (response.status === 200 || response.status === 201) {
        const policyId = response.data?.id ?? response.data?._id
        if (policyId) {
          navigate(`/main/admin/policies/${policyId}`)
        } else {
          navigate('/main/admin/policies')
        }
      } else {
        GlobalSwal.fire({
          title: response.data?.message ?? lang.labels.error,
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoading(false)
    })
  }

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.administration, branch: lang.labels.createPolicy}, company: true})

    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (appServiceForUser) {
      loadCatalog(appServiceForUser.api_key)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appServiceForUser])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      <div className="card card-flush mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="fw-bolder">{lang.labels.createPolicy}</h3>
          </div>
        </div>
        <div className="card-body pt-0">
          <div className="mb-5">
            <label className="form-label fw-bolder">{lang.labels.name} *</label>
            <input
              type="text"
              className="form-control"
              placeholder={lang.labels.name}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="mb-5">
            <label className="form-label fw-bolder">{lang.labels.description}</label>
            <textarea
              className="form-control"
              placeholder={lang.labels.description}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>
        </div>
      </div>
      <div className="card card-flush mb-5">
        <div className="card-body">
          <StatementBuilder
            statements={statements}
            catalog={catalog}
            catalogLoading={catalogLoading}
            onChange={setStatements}
          />
        </div>
      </div>
      <div className="d-flex justify-content-end">
        <button className="btn btn-light me-3" type="button" onClick={() => navigate('/main/admin/policies')}>{lang.labels.cancel}</button>
        <button className="btn btn-primary" type="button" onClick={handleSubmit} disabled={loading || !isValid()}>
          {loading ? lang.labels.loading : lang.labels.save}
        </button>
      </div>
    </div>
  )
};

export default PolicyCreatePage;
